import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { Movie } from "@/mocks/movies";

type HeroCarouselProps = {
  items: Movie[];
};

export default function HeroCarousel({ items }: HeroCarouselProps) {
  const [index, setIndex] = useState(0);
  const count = items.length;

  const go = useCallback(
    (next: number) => {
      if (count === 0) return;
      setIndex((next + count) % count);
    },
    [count],
  );

  useEffect(() => {
    if (count <= 1) return;
    const timer = setInterval(() => setIndex((i) => (i + 1) % count), 6000);
    return () => clearInterval(timer);
  }, [count]);

  if (count === 0) return null;

  const active = items[index];

  return (
    <section className="relative h-[440px] w-full overflow-hidden md:h-[580px]">
      {items.map((item, i) => (
        <div
          key={item.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            i === index ? "opacity-100" : "pointer-events-none opacity-0"
          }`}
        >
          <img
            src={item.cover}
            alt={`${item.title}焦点海报`}
            title={`${item.title} - 日韩伦理片推荐`}
            className="h-full w-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-primary-950/90 via-primary-950/60 to-primary-950/20" />
          <div className="absolute inset-0 bg-gradient-to-t from-primary-950/80 via-transparent to-transparent" />
        </div>
      ))}

      <div className="relative z-10 mx-auto flex h-full w-full max-w-none flex-col justify-end px-4 pb-16 md:px-8 md:pb-20">
        <div className="max-w-2xl animate-fade-up">
          <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-accent-400/40 bg-primary-950/40 px-3 py-1 text-xs font-medium text-accent-300 backdrop-blur">
            <i className="ri-fire-line" />
            本周焦点推荐
          </div>
          <h2 className="font-heading text-3xl font-bold leading-tight text-background-50 md:text-5xl">
            {active.title}
          </h2>
          <div className="mt-4 flex flex-wrap items-center gap-3 text-sm text-background-200">
            <span className="rounded-full bg-accent-500/90 px-2.5 py-0.5 text-xs font-semibold text-primary-950">
              {active.region}
            </span>
            {active.genres.map((g) => (
              <span key={g}>{g}</span>
            ))}
            <span className="text-background-300">{active.year}</span>
            <span className="flex items-center gap-1 text-accent-300">
              <i className="ri-star-fill" />
              {active.rating.toFixed(1)}
            </span>
          </div>
          <p className="mt-4 line-clamp-2 max-w-xl text-sm leading-relaxed text-background-200 md:text-base">
            {active.summary}
          </p>
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <Link
              to={`/detail/${active.id}`}
              className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              <i className="ri-play-circle-line text-lg" />
              查看剧情详情
            </Link>
            <Link
              to="/category"
              className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-50/40 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-background-50/10"
            >
              <i className="ri-grid-line text-lg" />
              浏览全部片库
            </Link>
          </div>
        </div>
      </div>

      <button
        type="button"
        onClick={() => go(index - 1)}
        aria-label="上一张"
        className="absolute left-3 top-1/2 z-20 hidden h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-background-50/30 bg-primary-950/40 text-background-50 backdrop-blur transition-colors hover:bg-primary-950/70 md:left-6 md:flex"
      >
        <i className="ri-arrow-left-s-line text-xl" />
      </button>
      <button
        type="button"
        onClick={() => go(index + 1)}
        aria-label="下一张"
        className="absolute right-3 top-1/2 z-20 hidden h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-background-50/30 bg-primary-950/40 text-background-50 backdrop-blur transition-colors hover:bg-primary-950/70 md:right-6 md:flex"
      >
        <i className="ri-arrow-right-s-line text-xl" />
      </button>

      <div className="absolute bottom-6 left-1/2 z-20 flex -translate-x-1/2 items-center gap-2">
        {items.map((item, i) => (
          <button
            key={item.id}
            type="button"
            aria-label={`切换到第${i + 1}张`}
            onClick={() => go(i)}
            className={`h-2 rounded-full transition-all ${
              i === index ? "w-6 bg-accent-400" : "w-2 bg-background-50/50"
            }`}
          />
        ))}
      </div>
    </section>
  );
}